/**
 * ACV vs RCV calculation engine for the public calculator.
 * Pure functions — fully tested in src/test/acvRcv.test.ts.
 */

import { getCategory, type DepreciationCurve } from "./categories";

export interface CalcItem {
  id: string;
  name: string;
  /** Category id from CALC_CATEGORIES */
  categoryId: string;
  /** Replacement cost per unit (today's dollars) */
  rcv: number;
  /** Age in years at "now" */
  ageYears: number;
  /** Quantity multiplier */
  qty: number;
}

export interface ItemResult {
  item: CalcItem;
  rcvTotal: number;
  acvTotal: number;
  gap: number;
  depreciationPct: number;
  effectiveAge: number;
}

export interface Totals {
  rcv: number;
  acv: number;
  gap: number;
  /** acv / rcv (0–1). 0 if rcv is 0. */
  coverage: number;
}

/** Calculator-only floor: ACV never below this fraction of RCV. */
const ACV_FLOOR = 0.10;
const CAP_STRAIGHT = 0.90;
const CAP_ACCEL = 0.95;

export function depreciationFraction(
  ageYears: number,
  lifespan: number,
  curve: DepreciationCurve,
): number {
  const age = Math.max(0, ageYears);
  if (curve === "flat") return 0;
  if (lifespan <= 0) return 0;

  const ratio = age / lifespan;

  if (curve === "accelerated") {
    const raw = Math.pow(Math.min(ratio, 1.5), 0.7);
    return Math.min(raw, CAP_ACCEL);
  }

  return Math.min(ratio, CAP_STRAIGHT);
}

export function calculateItem(item: CalcItem, yearsForward = 0): ItemResult {
  const cat = getCategory(item.categoryId);
  const effectiveAge = item.ageYears + yearsForward;
  const dep = depreciationFraction(effectiveAge, cat.lifespan, cat.curve);
  const qty = Math.max(1, item.qty || 1);
  const rcvTotal = item.rcv * qty;
  const acvRaw = rcvTotal * (1 - dep);
  const acvTotal = Math.max(acvRaw, rcvTotal * ACV_FLOOR);
  return {
    item,
    rcvTotal,
    acvTotal,
    gap: rcvTotal - acvTotal,
    depreciationPct: rcvTotal > 0 ? Math.round((1 - acvTotal / rcvTotal) * 100) : 0,
    effectiveAge,
  };
}

export function calculateAll(items: CalcItem[], yearsForward = 0): ItemResult[] {
  return items.map((i) => calculateItem(i, yearsForward));
}

export function calculateTotals(items: CalcItem[], yearsForward = 0): Totals {
  const results = calculateAll(items, yearsForward);
  const rcv = results.reduce((s, r) => s + r.rcvTotal, 0);
  const acv = results.reduce((s, r) => s + r.acvTotal, 0);
  return {
    rcv,
    acv,
    gap: rcv - acv,
    coverage: rcv > 0 ? acv / rcv : 0,
  };
}

export function formatUSD(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Math.round(n));
}
